/**
 * Write every Pages deck the way the spec reads it: page 0 active, the rest
 * inactive (PAGES_SPEC §3 "Initial state").
 *
 * The editor shows one page at a time by toggling the page objects' `active`,
 * and a save serialized whatever was showing — so a deck saved while the
 * author was viewing page 2 arrived with page 2 enabled and page 1 disabled.
 * Every runtime starts at index 0, so the first tap moved the index onto the
 * page already on screen: a visual no-op (Trace#2064, trace-viewer#809).
 * Which page the author was looking at is editor state; the file's meaning is
 * "this deck, starting at its first page".
 *
 * Runs on the flat export records, before nesting. Bound `active` values
 * (`$ref` strings) are left alone — a binding is authored intent, not a view.
 *
 * WHICH RECORDS ARE PAGES is not decided here. When the export has its live
 * objects (every export with a TraceScene), the deck's own behavior is asked —
 * `getPageNodes()`, the same call the runtime pages with — and the answer is
 * mapped back to records by transform identity. Names are not unique in the
 * hierarchy (two decks in different subtrees can both be "Deck"), so a
 * name-keyed lookup would let one deck's pages decide another's first page.
 * The serialized-parent fallback below exists only for an export with no live
 * objects, and refuses to guess when the deck's name is ambiguous.
 */
import type { SceneObjectData } from '../scenedata/sceneobjectdata';
import type { PagesData } from '../scenedata/behaviors/pagesdata';
import type { SceneObject } from '../core/SceneObject';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Pages } from '../behaviors/pages';

const pathOf = (r: SceneObjectData): string => (r.transform?.parent ? `${r.transform.parent}/${r.name}` : r.name);

/** A record is a child of `deck` under either parent convention the exporters use: a full path or a bare name. */
const isChildOf = (r: SceneObjectData, deck: SceneObjectData, deckPath: string): boolean => {
    const parent = r.transform?.parent;
    if (!parent) return false;
    return parent === deckPath || parent === deck.name || parent.endsWith(`/${deck.name}`);
};

/**
 * The deck's pages as records, or `null` when they cannot be known — the file
 * is then left exactly as it was rather than written from a guess.
 */
function pagesOf(
    deck: SceneObjectData,
    pages: Pick<PagesData, 'pageSource' | 'shared'>,
    records: SceneObjectData[],
    live: SceneObject | undefined,
    recordByTransform: Map<TransformNode, SceneObjectData>,
    nameCount: Map<string, number>,
): SceneObjectData[] | null {
    // The deck's own page set, resolved the way the runtime resolves it
    // (`shared` excluded, `pageSource` honoured) — asked for by class so a
    // rename cannot silently turn this whole pass into a no-op.
    const nodes = live?.getBehavior(Pages)?.getPageNodes();
    if (nodes) {
        // A page with no record of its own is not this pass's to write: a
        // `pageSource: 'firstChild'` deck pages over a GLB's internal nodes,
        // which are raw TransformNodes and are never serialized as their own
        // records (PAGES_SPEC §3, P-13), and a subtree export can leave a page
        // outside the file. Either way the page set is incomplete here, so the
        // deck is skipped and the runtime's own enforcement covers it.
        const recs = nodes.map(n => recordByTransform.get(n));
        return recs.every((r): r is SceneObjectData => r !== undefined) ? recs : null;
    }

    // No live deck (an export built without a TraceScene): all this pass has
    // is the serialized parent strings. Names are not identity, so a deck
    // whose name is shared with another record could collect that one's
    // children — refuse rather than rewrite the wrong pages.
    if ((nameCount.get(deck.name) ?? 0) > 1) return null;

    const deckPath = pathOf(deck);
    if (pages.pageSource === 'firstChild') {
        const first = records.find(r => isChildOf(r, deck, deckPath));
        return first ? records.filter(r => isChildOf(r, first, pathOf(first))) : [];
    }
    const shared = new Set((pages.shared ?? []).map(p => p.split('/').pop()));
    return records.filter(r => isChildOf(r, deck, deckPath) && !shared.has(r.name));
}

/**
 * @param records   the flat export records, mutated in place
 * @param liveByRecord  record → live SceneObject, when the export has them
 * @returns how many decks were written — every deck whose pages this pass
 *          could identify, NOT how many were wrong. The pass does not diff
 *          first: an already-conformant deck is rewritten to the values it
 *          already had, which costs nothing and keeps the write path simple.
 */
export function normalizePageDecks(records: SceneObjectData[], liveByRecord?: WeakMap<SceneObjectData, SceneObject>): number {
    const recordByTransform = new Map<TransformNode, SceneObjectData>();
    // Counted once here rather than rescanned per deck: the fallback below
    // needs to know whether a deck's name is ambiguous among the records.
    const nameCount = new Map<string, number>();
    for (const r of records) {
        nameCount.set(r.name, (nameCount.get(r.name) ?? 0) + 1);
        const live = liveByRecord?.get(r);
        if (live?.transform) recordByTransform.set(live.transform, r);
    }

    let decks = 0;
    for (const deck of records) {
        const pages = deck.behaviors?.find(b => (b as { '@type'?: string })['@type'] === 'Pages') as
            | Pick<PagesData, 'pageSource' | 'shared'>
            | undefined;
        if (!pages) continue;

        const pageRecords = pagesOf(deck, pages, records, liveByRecord?.get(deck), recordByTransform, nameCount);
        if (!pageRecords || pageRecords.length === 0) continue;
        if (pageRecords.some(r => typeof r.active === 'string')) continue;

        pageRecords.forEach((r, i) => { r.active = i === 0; });
        decks += 1;
    }
    return decks;
}
